import React from 'react';
import { ActivityIndicator, FlatList, RefreshControl, StyleSheet, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import Text from '../global/Text';
import fontMaker from '../../utils/font';
import { ReportSummaryLite } from '../../store/reducers/siteReport/type';
import ReportsListItem from './ReportsListItem';

type ReportsListProps = {
  reports: ReportSummaryLite[];
  loading: boolean;
  hasMore: boolean;
  profileColor: string;
  onRefresh: () => void;
  onLoadMore: () => void;
};

const ReportsList = ({ reports, loading, hasMore, profileColor, onRefresh, onLoadMore }: ReportsListProps) => {
  const { t } = useTranslation('home');

  const renderItem = ({ item }: { item: ReportSummaryLite }) => <ReportsListItem item={item} />;

  const keyExtractor = (item: ReportSummaryLite, index: number) =>
    `lite-report-${item.key ?? item.questionnaireName}-${index}`;

  const ListEmptyComponent = loading ? null : (
    <View style={styles.emptyContainer}>
      <Text style={styles.emptyTitle}>{t('reports.noReports')}</Text>
      <Text style={styles.emptySubtitle}>{t('reports.noReportsHint')}</Text>
    </View>
  );

  const ListFooterComponent = hasMore ? (
    <View style={styles.footerLoader}>
      <ActivityIndicator color={profileColor} size="small" />
    </View>
  ) : null;

  return (
    <FlatList
      data={reports}
      renderItem={renderItem}
      keyExtractor={keyExtractor}
      onEndReached={onLoadMore}
      onEndReachedThreshold={0.3}
      ListEmptyComponent={ListEmptyComponent}
      ListFooterComponent={ListFooterComponent}
      refreshControl={
        <RefreshControl refreshing={loading && reports.length === 0} onRefresh={onRefresh} tintColor={profileColor} />
      }
      contentContainerStyle={[styles.listContent, reports.length === 0 && styles.emptyFlex]}
    />
  );
};

export default ReportsList;

const styles = StyleSheet.create({
  emptyFlex: {
    flex: 1
  },
  listContent: {
    paddingTop: 2,
    paddingBottom: 100
  },
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 60
  },
  emptyTitle: {
    fontSize: 18,
    color: '#25282B',
    ...fontMaker({ weight: 'Bold' }),
    marginBottom: 8
  },
  emptySubtitle: {
    fontSize: 13,
    color: '#7C7C7C',
    textAlign: 'center',
    width: 240
  },
  footerLoader: {
    paddingVertical: 16,
    alignItems: 'center'
  }
});
